import Vue from 'vue';
import {
  Action,
  getModule,
  Module,
  Mutation,
  VuexModule
} from 'vuex-module-decorators';

import UsersApi from '@/api/users';
import store from '@/store';

const name = 'user';

@Module({
  namespaced: true,
  name,
  dynamic: true, // important!
  store, // module is dynamically added to the store here (so after the store was created, instead of during the creation)
  preserveState: false
})
class UserModule extends VuexModule {
  // state
  data = {} as User;
  location = {} as CustomerLocation;
  rewards = [] as LoyaltyReward[];
  availableLoyaltyRewards = [] as LoyaltyAccountReward[];

  // getters
  get loggedIn() {
    return !!this.data?.email;
  }

  get loyaltyAccountRewards() {
    return this.availableLoyaltyRewards;
  }

  get customerLocation() {
    return {
      coords: {
        latitude: this.location?.latitude,
        longitude: this.location?.longitude,
      },
      hasCoords: this.location?.latitude && this.location?.longitude
    }
  }

  get address(): CustomerLocation {
    return this.location;
  }

  get defaultDeliveryAddress() {
    // TODO: Check isDefault property
    return this.data?.savedAddresses?.find((addr) => addr.isDefault);
  }

  get savedDeliveryAddresses() {
    return this.data.savedAddresses;
  }

  get fullName() {
    return `${this.data?.firstName} ${this.data?.lastName}`;
  }

  get firstName() {
    return this.data?.firstName;
  }

  get lastName() {
    return this.data?.lastName;
  }

  get email() {
    return this.data?.email;
  }

  get phoneNumber() {
    return this.data?.phoneNumber;
  }

  get currentPoints() {
    return this.data?.currentPoints;
  }

  get pointsUntilReward() {
    return this.data?.pointsUntilReward;
  }

  get availableRewards() {
    return this.rewards;
  }

  get savedPaymentDetails() {
    return this.data.savedPaymentDetails;
  }

  get orderHistory() {
    return this.data.orderHistory;
  }

  get rewardHistory() {
    return this.data.rewardHistory;
  }

  get favoriteOrders() {
    return this.data.favoriteOrders;
  }

  get favoriteRestaurants() {
    return this.data.favoriteRestaurants || [];
  }

  // mutations
  // TODO~> create MutationAction that sets user,
  // checks if there is a basket
  // if there is one, call to get rewards
  // else set the user as is, setting basket should check if there is a user and call for rewards otherwise
  // Also, if restaurant changes, refresh rewards
  @Mutation
  setUser(user: User) {
    if (typeof user !== 'object') {
      return Vue.$log.error('attemting to set a user value that is not an object', user);
    }

    this.data = {
      ...this.data,
      ...user
    };
  }

  @Mutation
  setLoyaltyRewards(rewards: LoyaltyAccountReward[]) {
    this.availableLoyaltyRewards = rewards;
  }

  @Mutation
  setLocation(location: CustomerLocation) {
    this.location = {
      ...this.location,
      ...location,
    };
  }

  @Mutation
  setRewards(rewards: LoyaltyReward[]) {
    this.rewards = rewards;
  }

  // @Mutation
  // setFavoriteLocations(locations: FavoriteLocationModel[]) {
  //   this.data = {
  //     ...this.data,
  //     favoriteLocations: locations
  //   };
  // }

  @Mutation
  addFavoriteLocationToCustomer(location: FavoriteRestaurant) {
    const currLocations = this.data.favoriteRestaurants.slice();
    const currentLocation = currLocations.find((favoriteLocation) => favoriteLocation.id === location.id)
    if (currentLocation) {
      const locationIndex = currLocations.indexOf(currentLocation);
      if (locationIndex > -1) {
        currLocations.splice(locationIndex, 1);
      }
    }

    this.data = {
      ...this.data,
      favoriteRestaurants: [
        ...currLocations,
        location
      ]
    }
  }

  @Mutation
  removeFavoriteLocationFromCustomer(location: FavoriteRestaurant) {
    const currLocations = this.data.favoriteRestaurants.slice();
    const locationIndex = currLocations.indexOf(location);
    if (locationIndex > -1) {
      currLocations.splice(locationIndex, 1);
      this.data = {
        ...this.data,
        favoriteRestaurants: currLocations
      };
    }
  }

  @Mutation
  setFavoriteOrders(orders: FavoriteOrderModel[]) {
    this.data = {
      ...this.data,
      favoriteOrders: orders
    };
  }

  @Mutation
  setSavedPaymentDetails(accounts: BillingAccount[] | null) {
    this.data = {
      ...this.data,
      savedPaymentDetails: accounts
    };
  }

  // actions
  @Action({ commit: 'setRewards' })
  async getRewards(restaurantId: number) {
    try {
      Vue.$log.info('getRewards', restaurantId);
      return await UsersApi.getRewards(restaurantId);
    } catch (error) {
      Vue.$log.error(error.data);
      return this.context.getters.data;
    }
  }

  @Action
  async getFavoriteLocations() {
    return UsersApi.getFavoriteLocations();
  }

  @Action
  async addFavoriteLocation({ restaurantId, isDefault }: { restaurantId: number; isDefault: boolean }) {
    return UsersApi.addFavoriteLocation(restaurantId, isDefault);
  }

  @Action
  async removeFavoriteLocation(favoriteId: number) {
    return UsersApi.removeFavoriteLocation(favoriteId);
  }

  @Action({ commit: 'setFavoriteOrders' })
  async getFavoriteOrders() {
    return UsersApi.getFavoriteOrders();
  }

  @Action({ commit: 'setFavoriteOrders' })
  async addFavoriteOrder({ basketId, description, isDefault }: { basketId: string; description: string; isDefault: boolean }) {
    return UsersApi.addFavoriteOrder({ basketId: basketId, description: description, isDefault: isDefault });
  }

  @Action({ commit: 'setFavoriteOrders' })
  async removeFavoriteOrder(faveId: number) {
    const currentFavorites = this.data.favoriteOrders.slice();
    const favorite = currentFavorites.find(x => x.id === faveId);
    if (favorite) {
      const favoriteIndex = currentFavorites.indexOf(favorite);
      if (favoriteIndex > -1) {
        currentFavorites.splice(favoriteIndex, 1);
      }
    }
    await UsersApi.removeFavoriteOrder(faveId);
    return currentFavorites;
  }

  @Action
  async setDefaultDeliveryAddress(addressId: number) {
    return UsersApi.setDefaultDeliveryAddress(addressId);
  }

  @Action({ rawError: true })
  async submitClaimRewardRequest(request: ClaimRewardRequest) {
    return UsersApi.submitClaimRewardRequest(request);
  }

  @Action({ commit: 'setSavedPaymentDetails', rawError: true })
  async getBillingAccounts(basketId: string) {
    return UsersApi.getBillingAccounts(basketId);
  }
}
export default getModule(UserModule);
